import React, { useState, useRef, useEffect } from 'react';
import './SupportWidget.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeadset, faTimes, faArrowLeft, faArrowRight,
  faBoxOpen, faFileInvoiceDollar, faChartLine,
  faShieldHalved, faGears, faPhone,
  faLightbulb, faCircleCheck
} from '@fortawesome/free-solid-svg-icons';

// ── Chatbot Flow Data ─────────────────────────────────────
const CATEGORIES = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    desc: 'Setup & first steps',
    icon: faLightbulb,
    color: 'blue',
    questions: [
      {
        q: 'How do I create my first company?',
        a: 'Go to Settings → Company Setup → New Company. Fill in your company name, GST number, address, and financial year details. Click "Save" and your company is ready to use!',
        steps: ['Open Settings from the sidebar', 'Click "Company Setup"', 'Fill in company details & GST info', 'Set financial year start date', 'Click "Save & Continue"'],
      },
      {
        q: 'How to add users & assign roles?',
        a: 'Navigate to Settings → User Management → Add User. Enter the user\'s name, email, and phone number. Then assign a role (Admin, Operator, Viewer) to control their access level.',
        steps: ['Go to Settings → User Management', 'Click "Add New User"', 'Enter user details', 'Select role & permissions', 'Click "Invite User"'],
      },
      {
        q: 'How do I set up my product catalog?',
        a: 'Go to Inventory → Products → Add Product. Enter product name, HSN code, tax slab, unit of measure, and pricing. You can also bulk-import products from Excel.',
        steps: ['Navigate to Inventory → Products', 'Click "Add Product" or "Bulk Import"', 'Fill in product details, HSN & tax', 'Set MRP, purchase price & selling price', 'Save the product'],
      },
    ],
  },
  {
    id: 'billing',
    label: 'Billing & Invoicing',
    desc: 'Sales, purchases & GST',
    icon: faFileInvoiceDollar,
    color: 'teal',
    questions: [
      {
        q: 'How to create a sales invoice?',
        a: 'Go to Sales → New Invoice. Select the customer, add products, apply discounts if needed, choose payment mode, and click "Save & Print". The invoice is auto-numbered and GST-compliant.',
        steps: ['Go to Sales → New Invoice', 'Select or create customer', 'Add products using search or barcode', 'Apply discount/tax adjustments', 'Choose payment mode & save'],
      },
      {
        q: 'How to handle sales returns?',
        a: 'Navigate to Sales → Credit Note. Reference the original invoice, select items being returned, specify the reason, and process the refund. Stock is automatically updated.',
        steps: ['Go to Sales → Credit Note', 'Reference the original invoice number', 'Select returned items & quantity', 'Enter reason for return', 'Process refund & update stock'],
      },
      {
        q: 'How to create purchase bills?',
        a: 'Go to Purchase → New Purchase Bill. Add supplier details, enter items with quantities and rates, apply GST, and save. The bill auto-updates your inventory and accounts payable.',
        steps: ['Navigate to Purchase → New Purchase Bill', 'Select or add supplier', 'Enter items, quantities & rates', 'Verify GST calculation', 'Save & update inventory'],
      },
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory Management',
    desc: 'Stock, barcode & tracking',
    icon: faBoxOpen,
    color: 'purple',
    questions: [
      {
        q: 'How to manage stock across branches?',
        a: 'Use Inventory → Stock Transfer to move stock between branches. Select source branch, destination branch, items, and quantities. Both branches update in real-time.',
        steps: ['Go to Inventory → Stock Transfer', 'Select source & destination branch', 'Add items to transfer', 'Enter quantities & verify', 'Confirm transfer'],
      },
      {
        q: 'How to set up barcode scanning?',
        a: 'Connect your barcode scanner (USB/Bluetooth). Go to Settings → Barcode Configuration. Enable barcode scanning mode. Products are auto-detected when scanned during billing.',
        steps: ['Connect barcode scanner to device', 'Go to Settings → Barcode Config', 'Enable barcode scanning', 'Test scan with a product', 'Use in POS billing'],
      },
      {
        q: 'How to set low-stock alerts?',
        a: 'Go to Inventory → Settings → Reorder Levels. Set minimum stock quantity for each product. You\'ll get notifications when stock falls below the threshold.',
        steps: ['Navigate to Inventory → Settings', 'Click "Reorder Levels"', 'Set minimum quantity per product', 'Enable alert notifications', 'Save settings'],
      },
    ],
  },
  {
    id: 'reports',
    label: 'GST & Reports',
    desc: 'Returns, analytics & filing',
    icon: faChartLine,
    color: 'green',
    questions: [
      {
        q: 'How to generate GSTR-1 report?',
        a: 'Go to Reports → GST → GSTR-1. Select the filing period. The system auto-populates all B2B, B2C, and other invoice data. Review, reconcile, and export as JSON for filing.',
        steps: ['Navigate to Reports → GST', 'Select GSTR-1', 'Choose the filing period', 'Review auto-populated data', 'Export JSON for GST portal'],
      },
      {
        q: 'How to view profit & loss statement?',
        a: 'Go to Reports → Financial → Profit & Loss. Select the date range. The report shows income, expenses, gross profit, net profit, and detailed breakdowns by category.',
        steps: ['Go to Reports → Financial', 'Select "Profit & Loss"', 'Set date range', 'View income vs expenses', 'Export or print the report'],
      },
      {
        q: 'How to track daily sales summary?',
        a: 'Access Dashboard → Sales Overview or Reports → Daily Summary. View total sales, collection, pending amounts, top products, and hourly sales trends for any selected date.',
        steps: ['Open Dashboard or Reports', 'Select "Daily Summary"', 'Choose the date', 'View sales, collections & trends', 'Print or share the report'],
      },
    ],
  },
  {
    id: 'settings',
    label: 'Account & Settings',
    desc: 'Security, backup & config',
    icon: faShieldHalved,
    color: 'amber',
    questions: [
      {
        q: 'How do I reset my password?',
        a: 'Go to the login screen → "Forgot Password". Enter your registered email or phone number. A reset link/OTP will be sent. Follow the instructions to set a new password.',
        steps: ['Go to login screen', 'Click "Forgot Password"', 'Enter registered email/phone', 'Check inbox for reset link', 'Set new password'],
      },
      {
        q: 'How to backup my data?',
        a: 'Auto-backups run daily on the cloud. For manual backup: Settings → Backup & Restore → "Create Backup Now". Data is encrypted and stored on secure AWS servers.',
        steps: ['Go to Settings → Backup & Restore', 'Click "Create Backup Now"', 'Wait for backup to complete', 'Download local copy (optional)', 'Verify backup in history'],
      },
      {
        q: 'How to customize invoice template?',
        a: 'Navigate to Settings → Invoice Templates. Choose from pre-built templates or customize your own. Add your logo, adjust colors, set terms & conditions, and preview before saving.',
        steps: ['Go to Settings → Invoice Templates', 'Select a template or create custom', 'Upload your company logo', 'Set colors, fonts & layout', 'Preview & save template'],
      },
    ],
  },
  {
    id: 'contact',
    label: 'Contact Support',
    desc: 'Talk to our team',
    icon: faPhone,
    color: 'coral',
    questions: [],
  },
];

const CONTACT_INFO = {
  phone: '+91 90320 32612',
  email: 'support@auditusierp.com',
  hours: 'Mon–Sat, 9 AM – 7 PM IST',
  whatsapp: '+91 90320 32612',
};

export default function SupportWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [step, setStep] = useState('home'); // 'home' | 'category' | 'answer'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showTyping, setShowTyping] = useState(false);
  const bodyRef = useRef(null);

  // Auto-scroll to bottom on content change
  useEffect(() => {
    if (bodyRef.current) {
      setTimeout(() => {
        bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
      }, 100);
    }
  }, [step, selectedCategory, selectedQuestion, showTyping]);

  const handleToggle = () => {
    if (isOpen) {
      setIsClosing(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsClosing(false);
      }, 250);
    } else {
      setIsOpen(true);
      setStep('home');
      setSelectedCategory(null);
      setSelectedQuestion(null);
    }
  };

  const handleCategoryClick = (cat) => {
    if (cat.id === 'contact') {
      setSelectedCategory(cat);
      setStep('contact');
      return;
    }
    setShowTyping(true);
    setSelectedCategory(cat);
    setTimeout(() => {
      setShowTyping(false);
      setStep('category');
    }, 600);
  };

  const handleQuestionClick = (question) => {
    setShowTyping(true);
    setSelectedQuestion(question);
    setTimeout(() => {
      setShowTyping(false);
      setStep('answer');
    }, 700);
  };

  const handleBack = () => {
    if (step === 'answer') {
      setSelectedQuestion(null);
      setStep('category');
    } else {
      setSelectedCategory(null);
      setSelectedQuestion(null);
      setStep('home');
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning!';
    if (hour < 17) return 'Good afternoon!';
    return 'Good evening!';
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && !isClosing && <div className="sw-overlay" onClick={handleToggle} />}

      {/* Chat Panel */}
      {isOpen && (
        <div className={`sw-panel ${isClosing ? 'closing' : ''}`}>
          {/* Header */}
          <div className="sw-header">
            <div className="sw-header-top">
              <div className="sw-header-brand">
                <div className="sw-header-avatar">
                  <FontAwesomeIcon icon={faHeadset} />
                </div>
                <div className="sw-header-brand-info">
                  <h4>Auditus-iERP Support</h4>
                  <span><span className="sw-online-dot" /> Online now</span>
                </div>
              </div>
              <button className="sw-close-btn" onClick={handleToggle} aria-label="Close support chat">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="sw-header-greeting">
              <h3>{getGreeting()} 👋</h3>
              <p>How can we help you today?</p>
            </div>
          </div>

          {/* Body */}
          <div className="sw-body" ref={bodyRef}>
            <div className="sw-messages">

              {/* ── HOME: Categories ─────────────── */}
              {step === 'home' && (
                <>
                  <div className="sw-msg">
                    <div className="sw-msg-avatar">
                      <FontAwesomeIcon icon={faHeadset} />
                    </div>
                    <div className="sw-msg-bubble bot">
                      Choose a topic and I'll guide you through it instantly.
                    </div>
                  </div>
                  <div className="sw-options">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat.id}
                        className={`sw-option-btn sw-opt-${cat.color}`}
                        onClick={() => handleCategoryClick(cat)}
                      >
                        <div className="sw-option-icon">
                          <FontAwesomeIcon icon={cat.icon} />
                        </div>
                        <div className="sw-option-text">
                          <span>{cat.label}</span>
                          <span>{cat.desc}</span>
                        </div>
                        <FontAwesomeIcon icon={faArrowRight} className="sw-opt-arrow" />
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* ── CATEGORY: Questions ──────────── */}
              {step === 'category' && selectedCategory && (
                <>
                  <div className="sw-msg user">
                    <div className="sw-msg-avatar">
                      <FontAwesomeIcon icon={selectedCategory.icon} />
                    </div>
                    <div className="sw-msg-bubble">{selectedCategory.label}</div>
                  </div>
                  <div className="sw-msg">
                    <div className="sw-msg-avatar">
                      <FontAwesomeIcon icon={faHeadset} />
                    </div>
                    <div className="sw-msg-bubble bot">
                      Here are common questions about <strong>{selectedCategory.label}</strong>. Select one:
                    </div>
                  </div>
                  <div className="sw-options">
                    {selectedCategory.questions.map((q, i) => (
                      <button
                        key={i}
                        className={`sw-option-btn sw-opt-${selectedCategory.color}`}
                        onClick={() => handleQuestionClick(q)}
                      >
                        <div className="sw-option-icon">
                          <FontAwesomeIcon icon={faCircleCheck} />
                        </div>
                        <div className="sw-option-text">
                          <span>{q.q}</span>
                        </div>
                        <FontAwesomeIcon icon={faArrowRight} className="sw-opt-arrow" />
                      </button>
                    ))}
                  </div>
                  <div className="sw-actions-row">
                    <button className="sw-back-btn" onClick={handleBack}>
                      <FontAwesomeIcon icon={faArrowLeft} /> Back to topics
                    </button>
                  </div>
                </>
              )}

              {/* ── ANSWER ──────────────────────── */}
              {step === 'answer' && selectedQuestion && (
                <>
                  <div className="sw-msg user">
                    <div className="sw-msg-avatar">
                      <FontAwesomeIcon icon={faCircleCheck} />
                    </div>
                    <div className="sw-msg-bubble">{selectedQuestion.q}</div>
                  </div>
                  <div className="sw-msg">
                    <div className="sw-msg-avatar">
                      <FontAwesomeIcon icon={faHeadset} />
                    </div>
                    <div className="sw-msg-bubble bot">
                      Great question! Here's how:
                    </div>
                  </div>
                  <div className="sw-answer-card">
                    <h4><FontAwesomeIcon icon={faLightbulb} /> Answer</h4>
                    <p>{selectedQuestion.a}</p>
                    {selectedQuestion.steps && selectedQuestion.steps.length > 0 && (
                      <ul>
                        {selectedQuestion.steps.map((s, i) => (
                          <li key={i}><strong>Step {i + 1}:</strong> {s}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="sw-actions-row">
                    <button className="sw-back-btn" onClick={handleBack}>
                      <FontAwesomeIcon icon={faArrowLeft} /> More questions
                    </button>
                    <button className="sw-contact-btn" onClick={() => { setSelectedCategory(CATEGORIES.find(c => c.id === 'contact')); setStep('contact'); }}>
                      <FontAwesomeIcon icon={faPhone} /> Contact us
                    </button>
                  </div>
                </>
              )}

              {/* ── CONTACT ─────────────────────── */}
              {step === 'contact' && (
                <>
                  <div className="sw-msg user">
                    <div className="sw-msg-avatar">
                      <FontAwesomeIcon icon={faPhone} />
                    </div>
                    <div className="sw-msg-bubble">Contact Support</div>
                  </div>
                  <div className="sw-msg">
                    <div className="sw-msg-avatar">
                      <FontAwesomeIcon icon={faHeadset} />
                    </div>
                    <div className="sw-msg-bubble bot">
                      We're here to help! Reach us through any of these channels:
                    </div>
                  </div>
                  <div className="sw-answer-card">
                    <h4><FontAwesomeIcon icon={faHeadset} /> Contact Channels</h4>
                    <p>
                      📞 <strong>Phone:</strong> {CONTACT_INFO.phone}<br />
                      📧 <strong>Email:</strong> {CONTACT_INFO.email}<br />
                      💬 <strong>WhatsApp:</strong> {CONTACT_INFO.whatsapp}<br />
                      🕐 <strong>Hours:</strong> {CONTACT_INFO.hours}
                    </p>
                  </div>
                  <div className="sw-actions-row">
                    <button className="sw-back-btn" onClick={() => { setStep('home'); setSelectedCategory(null); }}>
                      <FontAwesomeIcon icon={faArrowLeft} /> Back to topics
                    </button>
                  </div>
                </>
              )}

              {/* ── Typing indicator ────────────── */}
              {showTyping && (
                <div className="sw-msg">
                  <div className="sw-msg-avatar">
                    <FontAwesomeIcon icon={faHeadset} />
                  </div>
                  <div className="sw-msg-bubble bot">
                    <div className="sw-typing">
                      <span className="sw-typing-dot" />
                      <span className="sw-typing-dot" />
                      <span className="sw-typing-dot" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="sw-footer">
            <span>
              <FontAwesomeIcon icon={faShieldHalved} />
              Powered by Auditus-iERP
            </span>
          </div>
        </div>
      )}

      {/* Floating Action Button */}
      <button
        className={`sw-fab ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
        aria-label="Open support chat"
        title="Need help? Chat with us!"
      >
        {!isOpen && <span className="sw-fab-badge">?</span>}
        <FontAwesomeIcon icon={isOpen ? faTimes : faHeadset} />
      </button>
    </>
  );
}
